import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { generateWeeklyPDF } from "@/lib/weeklyPdf";
import DatePicker from "@/components/DatePicker";
import { ChevronLeft, ChevronRight, Download, Printer, Filter, CalendarDays, X } from "lucide-react";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUS_LABEL = {
  draft: "Draft", submitted: "Submitted", changes_requested: "Changes Requested",
  rejected: "Rejected", approved: "Approved", published: "Published",
  updated_after_publication: "Updated After Publication", archived: "Archived",
};
const STATUS_PILL = {
  draft: "status-neutral", submitted: "status-info", changes_requested: "status-progress",
  rejected: "status-error", approved: "status-open", published: "status-published",
  updated_after_publication: "status-progress", archived: "status-neutral",
};

function startOfWeekMonday(d = new Date()) {
  const day = d.getDay();
  const diff = (day + 6) % 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - diff);
  return monday.toISOString().slice(0, 10);
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function humanRange(weekStart) {
  const start = new Date(weekStart + "T00:00:00");
  const end = new Date(start); end.setDate(start.getDate() + 6);
  const opts = { month: "long", day: "numeric", year: "numeric" };
  return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", opts)}`;
}
function shiftHours(s) {
  try {
    const [h1, m1] = s.start_time.split(":").map(Number);
    const [h2, m2] = s.end_time.split(":").map(Number);
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1) - (Number(s.break_minutes) || 0);
    return Math.max(0, mins / 60);
  } catch { return 0; }
}

export default function WeeklySchedule() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialWeek = params.get("week") || startOfWeekMonday();
  const [weekStart, setWeekStart] = useState(initialWeek);
  const [schedules, setSchedules] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [groupBy, setGroupBy] = useState("employee");
  const [department, setDepartment] = useState("All");
  const [role, setRole] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [preview, setPreview] = useState(null);

  useEffect(() => { setParams({ week: weekStart }); }, [weekStart, setParams]);

  const loadWeek = useCallback(() => {
    api.get("/schedules", { params: { week_start: weekStart } })
      .then(({ data }) => {
        setSchedules(data);
        if (data.length > 0) {
          const published = data.find((s) => s.status === "published" || s.status === "updated_after_publication");
          setSelectedId(published?.id || data[0].id);
        } else {
          setSelectedId("");
        }
      });
  }, [weekStart]);

  useEffect(() => { loadWeek(); }, [loadWeek]);
  useEffect(() => {
    if (["admin", "gm", "manager"].includes(user?.role)) {
      api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
    }
  }, [user]);

  const schedule = useMemo(() => schedules.find((s) => s.id === selectedId) || schedules[0], [schedules, selectedId]);

  const filteredShifts = useMemo(() => {
    const shifts = schedule?.shifts || [];
    return shifts.filter((s) => {
      if (department !== "All" && s.department !== department) return false;
      if (role && (s.role || "").toLowerCase() !== role.toLowerCase()) return false;
      if (selectedEmployees.length && !selectedEmployees.includes(s.employee_id)) return false;
      return true;
    });
  }, [schedule, department, role, selectedEmployees]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const byEmployee = useMemo(() => {
    const map = {};
    filteredShifts.forEach((s) => {
      (map[s.employee_id] = map[s.employee_id] || { emp_id: s.employee_id, name: s.employee_name, role: s.role, department: s.department, byDay: {} });
      (map[s.employee_id].byDay[s.date] = map[s.employee_id].byDay[s.date] || []).push(s);
    });
    return Object.values(map);
  }, [filteredShifts]);

  const totals = useMemo(() => {
    let hours = 0;
    const byDept = {};
    filteredShifts.forEach((s) => {
      const h = shiftHours(s);
      hours += h;
      byDept[s.department || "Other"] = (byDept[s.department || "Other"] || 0) + h;
    });
    return { hours, byDept, shifts: filteredShifts.length, employees: byEmployee.length };
  }, [filteredShifts, byEmployee]);

  const groups = useMemo(() => {
    if (groupBy === "department") {
      const m = {};
      byEmployee.forEach((e) => { (m[e.department || "Other"] = m[e.department || "Other"] || []).push(e); });
      return Object.entries(m);
    }
    if (groupBy === "role") {
      const m = {};
      byEmployee.forEach((e) => { (m[e.role || "Other"] = m[e.role || "Other"] || []).push(e); });
      return Object.entries(m);
    }
    return [["All employees", byEmployee]];
  }, [byEmployee, groupBy]);

  const goPrev = () => setWeekStart(addDays(weekStart, -7));
  const goNext = () => setWeekStart(addDays(weekStart, 7));
  const goToday = () => setWeekStart(startOfWeekMonday());

  const canDownload = ["admin", "gm", "manager"].includes(user?.role) || false;

  const openPreview = () => setPreview({
    weekStart, department, role, employees: selectedEmployees,
    includeNotes: true, includeBreaks: true, compact: false,
  });

  const departments = useMemo(() => {
    const s = new Set(["All"]);
    (schedule?.shifts || []).forEach((sh) => sh.department && s.add(sh.department));
    return Array.from(s);
  }, [schedule]);
  const roles = useMemo(() => {
    const s = new Set();
    (schedule?.shifts || []).forEach((sh) => sh.role && s.add(sh.role));
    return Array.from(s);
  }, [schedule]);

  return (
    <div className="space-y-6" data-testid="weekly-root">
      <div>
        <div className="label-caps">Workforce</div>
        <h1 className="font-heading text-3xl sm:text-4xl">Weekly Staff Schedule</h1>
        <div className="text-[color:var(--text-secondary)] text-sm mt-1">{humanRange(weekStart)}</div>
      </div>

      <div className="hos-card p-4 flex flex-wrap items-center gap-2">
        <button onClick={goPrev} className="hos-btn-outline flex items-center gap-1 py-2 px-3" data-testid="prev-week"><ChevronLeft size={14}/> Prev</button>
        <button onClick={goToday} className="hos-btn-outline py-2 px-3" data-testid="this-week">This Week</button>
        <button onClick={goNext} className="hos-btn-outline flex items-center gap-1 py-2 px-3" data-testid="next-week">Next <ChevronRight size={14}/></button>
        <label className="ml-2 flex items-center gap-2">
          <CalendarDays size={14} className="text-[color:var(--gold)]"/>
          <DatePicker value={weekStart} onChange={setWeekStart} testId="week-input" className="py-2 min-w-[220px]"/>
        </label>
        <div className="flex-1" />
        {schedules.length > 1 && (
          <select className="hos-input py-2 max-w-[260px]" value={selectedId} onChange={(e) => setSelectedId(e.target.value)} data-testid="schedule-select">
            {schedules.map((s) => <option key={s.id} value={s.id}>{`${s.schedule_number} · ${STATUS_LABEL[s.status]}`}</option>)}
          </select>
        )}
      </div>

      {schedule ? (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`status-pill ${STATUS_PILL[schedule.status] || "status-neutral"}`} data-testid="week-status">
              {STATUS_LABEL[schedule.status] || schedule.status}
            </span>
            <span className="text-xs text-[color:var(--text-secondary)]">{schedule.schedule_number} · v{schedule.version || 1}</span>
            <Link to={`/schedules/${schedule.id}`} className="text-xs text-[color:var(--gold)] underline">View details</Link>
            <div className="flex-1"/>
            {canDownload && (
              <>
                <button className="hos-btn-outline flex items-center gap-2" onClick={() => window.print()} data-testid="print-btn"><Printer size={16}/> Print</button>
                <button className="hos-btn-primary flex items-center gap-2" onClick={openPreview} data-testid="download-pdf-btn"><Download size={16}/> Download PDF</button>
              </>
            )}
          </div>

          <div className="hos-card p-4 flex flex-wrap gap-3 items-end">
            <Filter size={16} className="text-[color:var(--gold)] mb-3"/>
            <div>
              <label className="label-caps">Group by</label>
              <select className="hos-input py-2" value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="group-by">
                <option value="employee">Employee</option>
                <option value="department">Department</option>
                <option value="role">Job Role</option>
              </select>
            </div>
            <div>
              <label className="label-caps">Department</label>
              <select className="hos-input py-2" value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="filter-dept">
                {departments.map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps">Role</label>
              <select className="hos-input py-2" value={role} onChange={(e) => setRole(e.target.value)} data-testid="filter-role">
                <option value="">All</option>
                {roles.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div className="hos-card overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="text-left label-caps sticky top-0 bg-[color:var(--bg-surface)]">
                <tr>
                  <th className="p-3 sticky left-0 bg-[color:var(--bg-surface)] z-10 min-w-[180px]">Employee</th>
                  {DAY_LABELS.map((d, i) => (
                    <th key={d} className="p-3 min-w-[130px]">
                      {d}<br/><span className="text-[10px] text-[color:var(--text-secondary)] font-normal">{days[i]}</span>
                    </th>
                  ))}
                  <th className="p-3 min-w-[70px]">Hours</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(([groupName, list]) => (
                  <React.Fragment key={groupName}>
                    {groupBy !== "employee" && (
                      <tr className="border-t border-[color:var(--border)] bg-[color:var(--bg-surface-2)]">
                        <td className="p-2 font-bold text-[color:var(--gold)] cursor-pointer" colSpan={9}
                            onClick={() => setCollapsed((c) => ({ ...c, [groupName]: !c[groupName] }))}
                            data-testid={`group-${groupName}`}>
                          {collapsed[groupName] ? "▸" : "▾"} {groupName} ({list.length})
                        </td>
                      </tr>
                    )}
                    {!collapsed[groupName] && list.map((e) => {
                      const total = Object.values(e.byDay).flat().reduce((sum, s) => sum + shiftHours(s), 0);
                      return (
                        <tr key={e.emp_id} className="border-t border-[color:var(--border)]" data-testid={`row-${e.emp_id}`}>
                          <td className="p-3 sticky left-0 bg-[color:var(--bg-surface)] z-10">
                            <div className="font-semibold">{e.name}</div>
                            <div className="text-xs text-[color:var(--text-secondary)]">{e.role || "—"}{e.department ? " · " + e.department : ""}</div>
                          </td>
                          {days.map((d) => (
                            <td key={d} className="p-2 align-top">
                              {(e.byDay[d] || []).length === 0 ? (
                                <span className="italic text-[color:var(--text-disabled)]">OFF</span>
                              ) : (
                                <div className="space-y-1">
                                  {e.byDay[d].map((s, i) => (
                                    <div key={i} className="rounded-md px-2 py-1 bg-[color:var(--bg-surface-2)] border-l-2" style={{ borderColor: shiftColor(s.shift_type) }}>
                                      <div className="font-mono text-[color:var(--gold)]">{s.start_time}–{s.end_time}</div>
                                      <div className="text-[10px] text-[color:var(--text-secondary)]">{s.shift_type}{s.break_minutes ? ` · ${s.break_minutes}m break` : ""}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                          ))}
                          <td className="p-2 text-center font-bold text-[color:var(--gold)]">{total.toFixed(1)}h</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
                {byEmployee.length === 0 && (
                  <tr><td colSpan={9} className="p-10 text-center text-[color:var(--text-secondary)]">No shifts match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="hos-card p-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><div className="label-caps">Employees</div><div className="text-2xl font-heading">{totals.employees}</div></div>
            <div><div className="label-caps">Shifts</div><div className="text-2xl font-heading">{totals.shifts}</div></div>
            <div><div className="label-caps">Total hours</div><div className="text-2xl font-heading">{totals.hours.toFixed(1)}h</div></div>
            <div>
              <div className="label-caps">By dept</div>
              <div className="text-xs mt-1 space-y-0.5">
                {Object.entries(totals.byDept).map(([d, h]) => <div key={d}>{d}: {h.toFixed(1)}h</div>)}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="hos-card p-10 text-center text-[color:var(--text-secondary)]">
          <CalendarDays className="mx-auto text-[color:var(--gold)]" size={28}/>
          <div className="mt-3">No schedule exists for the week of {humanRange(weekStart)} yet.</div>
        </div>
      )}

      {preview && (
        <PrintPreview
          schedule={schedule} weekStart={weekStart}
          employees={employees}
          initial={preview}
          departments={departments} roles={roles} allEmployees={employees}
          onClose={() => setPreview(null)}
          onGenerate={async (opts) => {
            try {
              const filename = await generateWeeklyPDF({
                schedule, weekStart, employees,
                filters: { department: opts.department, role: opts.role, employees: opts.employees },
                generatedBy: user?.name,
                compact: opts.compact, includeNotes: opts.includeNotes, includeBreaks: opts.includeBreaks,
              });
              // Log PDF generation to history
              api.post("/pdf_records", {
                schedule_id: schedule?.id,
                schedule_number: schedule?.schedule_number,
                schedule_version: schedule?.version,
                schedule_status: schedule?.status,
                week_start: weekStart,
                filename,
                filters: { department: opts.department, role: opts.role, employees: opts.employees },
                official: ["published", "updated_after_publication"].includes(schedule?.status),
              }).catch((e) => console.warn("PDF history log failed", e));
              toast.success(`Downloaded ${filename}`);
              setPreview(null);
            } catch (e) { toast.error(formatApiError(e)); }
          }}
        />
      )}
    </div>
  );
}

function shiftColor(t) {
  if (t === "opening") return "#F4A261";
  if (t === "closing") return "#457B9D";
  if (t === "custom") return "#E63946";
  return "#D4AF37";
}

function PrintPreview({ schedule, weekStart, initial, departments, roles, allEmployees, onClose, onGenerate }) {
  const [opts, setOpts] = useState({
    department: initial.department || "All",
    role: initial.role || "",
    employees: initial.employees || [],
    includeNotes: true, includeBreaks: true, compact: false,
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="hos-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl">Print Preview</h3>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div><div className="label-caps">Week</div><div>{weekStart}</div></div>
          <div><div className="label-caps">Schedule status</div><div>{schedule?.status?.replace(/_/g, " ")}</div></div>
          <div>
            <label className="label-caps">Department filter</label>
            <select className="hos-input" value={opts.department} onChange={(e) => setOpts({ ...opts, department: e.target.value })} data-testid="pp-dept">
              {departments.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Role filter</label>
            <select className="hos-input" value={opts.role} onChange={(e) => setOpts({ ...opts, role: e.target.value })} data-testid="pp-role">
              <option value="">All</option>
              {roles.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-caps">Employees (leave empty for all)</label>
            <div className="max-h-32 overflow-y-auto border border-[color:var(--border)] rounded-md p-2 space-y-1">
              {allEmployees.map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={opts.employees.includes(e.id)}
                         onChange={(evt) => setOpts({
                           ...opts,
                           employees: evt.target.checked
                             ? [...opts.employees, e.id]
                             : opts.employees.filter((x) => x !== e.id),
                         })}/>
                  {e.name} — {e.role || "—"}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 mt-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={opts.includeNotes} onChange={(e) => setOpts({ ...opts, includeNotes: e.target.checked })}/> Include shift notes</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={opts.includeBreaks} onChange={(e) => setOpts({ ...opts, includeBreaks: e.target.checked })}/> Include break info</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={opts.compact} onChange={(e) => setOpts({ ...opts, compact: e.target.checked })}/> Compact layout</label>
        </div>
        <div className="mt-4 p-3 rounded-lg bg-[color:var(--bg-base)] border border-[color:var(--border)] text-xs font-mono">
          Filename: House-of-Spells-Times-Square{opts.department && opts.department !== "All" ? `-${opts.department}` : ""}-Weekly-Schedule-{weekStart}.pdf
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button className="hos-btn-outline" onClick={onClose}>Cancel</button>
          <button className="hos-btn-outline flex items-center gap-2" onClick={() => window.print()}><Printer size={16}/> Print</button>
          <button className="hos-btn-primary flex items-center gap-2" onClick={() => onGenerate(opts)} data-testid="pp-download-btn"><Download size={16}/> Download PDF</button>
        </div>
      </div>
    </div>
  );
}
